/**
 * Seven columns for the selected week — day, covers total, event cards by start time
 * (POS2-1259 CP4).
 */
import type { DayCovers, OccupancyEvent } from "@/hooks/use-occupancy";
import { useTranslation } from "@/i18n/use-translation";
import { isoWeekday, orderedEvents, shortTime, TYPE_COLOURS } from "@/lib/occupancy";
import { WEEKDAY_LABEL_KEYS } from "@/lib/occupancy-labels";
import { cn } from "@/lib/utils";

/** Hoisted: the i18next eslint rule reads a string literal inside a `cn()` call in the render
 *  body as user-facing text, even though these are class names. */
const EVENT_CARD = "min-w-0 rounded-md border-l-2 bg-muted/40 p-1.5 text-left";
const EVENT_CARD_CLICKABLE = "hover:bg-muted";

export function WeekView({
  days,
  capacity,
  onEditEvent,
}: {
  days: DayCovers[];
  capacity: number | null;
  /** Absent for a viewer without `occupancy:update` — decision 26. The card is then plain
   *  markup, not a button that looks clickable and does nothing. */
  onEditEvent?: (event: OccupancyEvent) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const events = orderedEvents(day.events);
          const over = capacity !== null && day.total !== null && day.total > capacity;
          return (
            <div key={day.date} className="min-h-48 border-r border-border p-2 last:border-r-0">
              <div className="mb-2 flex items-baseline justify-between gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(WEEKDAY_LABEL_KEYS[isoWeekday(day.date)])} {Number(day.date.slice(8))}
                </span>
                <span
                  className={cn(
                    "text-xs font-bold tabular-nums",
                    over ? "text-destructive" : "text-foreground",
                  )}
                >
                  {day.total ?? "—"}
                </span>
              </div>

              {events.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">{t("occupancy.week.noEvents")}</p>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {events.map((event) => {
                    const start = shortTime(event.start_time);
                    const end = shortTime(event.end_time);
                    const card = cn(EVENT_CARD, onEditEvent && EVENT_CARD_CLICKABLE);
                    const style = { borderLeftColor: TYPE_COLOURS[event.event_type] };
                    const body = (
                      <>
                        <span className="block truncate text-[11px] font-semibold">
                          {event.name}
                        </span>
                        <span className="block text-[11px] tabular-nums text-muted-foreground">
                          {start && end ? `${start}–${end}` : (start ?? "")} · {event.covers}
                        </span>
                      </>
                    );
                    return onEditEvent ? (
                      <button
                        key={event.id}
                        type="button"
                        onClick={() => onEditEvent(event)}
                        className={card}
                        style={style}
                      >
                        {body}
                      </button>
                    ) : (
                      <div key={event.id} className={card} style={style}>
                        {body}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
